import React, { useRef, useEffect, useCallback, useState } from 'react'
import { useColorScheme } from '@mui/joy'
import { FetchedEmailAttachment } from '../../types/fetched-email/types'
import axios from 'axios'
import { buildThemedEmailHtml } from './utils/buildThemedEmailHtml'

const BATCH_SIZE = 6

interface AttachmentRequest {
    attachmentId: string
    messageId: string
    grantId: string
}

interface BatchResponseItem {
    attachmentId: string
    messageId: string
    data: string | null
    contentType: string | null
}

async function fetchAttachmentsBatch(requests: AttachmentRequest[]): Promise<Map<string, string>> {
    const result = new Map<string, string>()
    if (requests.length === 0) return result

    try {
        const response = await axios.post<{ success: boolean; data: BatchResponseItem[] }>('/email/attachments/batch', {
            attachments: requests,
        })

        for (const item of response.data.data) {
            if (item.data) {
                const contentType = item.contentType || 'application/octet-stream'
                result.set(`${item.attachmentId}:${item.messageId}`, `data:${contentType};base64,${item.data}`)
            }
        }
    } catch (error) {
        console.error('Failed to fetch attachments batch', error)
    }

    return result
}

async function loadAttachmentsInBatches(
    requests: AttachmentRequest[],
    cache: Map<string, string>,
    onLoaded: (attachmentId: string, dataUrl: string) => void,
    onError?: (attachmentId: string) => void
): Promise<void> {
    // Emit cached results immediately
    const uncached: AttachmentRequest[] = []
    for (const req of requests) {
        const cacheKey = `${req.attachmentId}:${req.messageId}`
        if (cache.has(cacheKey)) {
            onLoaded(req.attachmentId, cache.get(cacheKey)!)
        } else {
            uncached.push(req)
        }
    }

    // Fetch uncached in batches of BATCH_SIZE
    for (let i = 0; i < uncached.length; i += BATCH_SIZE) {
        const batch = uncached.slice(i, i + BATCH_SIZE)
        const results = await fetchAttachmentsBatch(batch)

        // Report loaded
        for (const [key, dataUrl] of results) {
            cache.set(key, dataUrl)
            const attachmentId = key.split(':')[0]
            onLoaded(attachmentId, dataUrl)
        }

        // Report failures (requested but not in results)
        if (onError) {
            for (const req of batch) {
                const cacheKey = `${req.attachmentId}:${req.messageId}`
                if (!results.has(cacheKey)) {
                    onError(req.attachmentId)
                }
            }
        }
    }
}

// Component to render image previews outside iframe
const ImagePreviewsOutsideIframe: React.FC<{
    attachments: FetchedEmailAttachment[]
    messageId: string
    cache: React.MutableRefObject<Map<string, string>>
}> = ({ attachments, messageId, cache }) => {
    const [loadedImages, setLoadedImages] = useState<{ [key: string]: string }>({})

    useEffect(() => {
        let cancelled = false

        const requests: AttachmentRequest[] = attachments.map((att) => ({
            attachmentId: att.id,
            messageId,
            grantId: att.grant_id,
        }))

        const errorPlaceholder =
            'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjVmNWY1IiBzdHJva2U9IiNkZGQiLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjE0IiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+RXJyb3IgbG9hZGluZyBpbWFnZTwvdGV4dD48L3N2Zz4='

        loadAttachmentsInBatches(
            requests,
            cache.current,
            (attachmentId, dataUrl) => {
                if (!cancelled) {
                    setLoadedImages((prev) => ({ ...prev, [attachmentId]: dataUrl }))
                }
            },
            (attachmentId) => {
                if (!cancelled) {
                    setLoadedImages((prev) => ({ ...prev, [attachmentId]: errorPlaceholder }))
                }
            }
        ).catch((error) => console.error('Failed to load attachment previews', error))

        return () => {
            cancelled = true
        }
    }, [attachments, messageId, cache])

    if (attachments.length === 0) return null

    return (
        <div style={{ marginTop: '16px', padding: '0 16px' }}>
            <h4
                style={{
                    fontFamily: 'Arial, sans-serif',
                    color: 'var(--joy-palette-text-secondary, #666)',
                    fontSize: '14px',
                    margin: '0 0 12px 0',
                }}
            >
                Attachments ({attachments.length})
            </h4>

            {attachments.map((attachment) => (
                <div
                    key={attachment.id}
                    style={{
                        margin: '16px 0',
                        textAlign: 'center',
                        padding: '12px',
                        border: '1px solid var(--joy-palette-divider)',
                        borderRadius: '8px',
                        backgroundColor: 'var(--joy-palette-background-surface)',
                    }}
                >
                    <img
                        src={
                            loadedImages[attachment.id] ||
                            'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjVmNWY1IiBzdHJva2U9IiNkZGQiLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjE0IiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+TG9hZGluZy4uLjwvdGV4dD48L3N2Zz4='
                        }
                        alt={attachment.filename || 'Image'}
                        style={{
                            maxWidth: '100%',
                            height: 'auto',
                            borderRadius: '4px',
                        }}
                    />
                    {attachment.filename && (
                        <p
                            style={{
                                fontSize: '12px',
                                color: 'var(--joy-palette-text-secondary, #666)',
                                margin: '8px 0 0 0',
                                fontWeight: '500',
                            }}
                        >
                            {attachment.filename}
                        </p>
                    )}
                </div>
            ))}
        </div>
    )
}

interface EmailBodyProps {
    body: string
    subject?: string
    height?: string | number
    width?: string | number
    setupIframeContent?: (iframe: HTMLIFrameElement, body: string) => void
    attachments?: FetchedEmailAttachment[]
    messageId: string
    cleanedBody?: string
    snippet?: string
}

/**
 * A reusable component for displaying email body content in an iframe
 */
const EmailBody: React.FC<EmailBodyProps> = ({
    body,
    subject,
    height = 'auto',
    width = '100%',
    setupIframeContent: externalSetupIframeContent,
    attachments = [],
    messageId,
    cleanedBody,
    snippet,
}) => {
    const iframeRef = useRef<HTMLIFrameElement>(null)
    const attachmentCache = useRef<Map<string, string>>(new Map())
    const { mode } = useColorScheme()
    const [foundInlineImageIds, setFoundInlineImageIds] = useState<Set<string>>(new Set())

    // Check if we have meaningful content to show in iframe
    const hasContent = useCallback(() => {
        if (cleanedBody && cleanedBody.trim() && cleanedBody.trim() !== 'No body') {
            return true
        }
        if (body && body.trim() && body.trim() !== 'No body') {
            return true
        }
        if (snippet && snippet.trim()) {
            return true
        }
        return false
    }, [cleanedBody, body, snippet])

    const internalSetupIframeContent = useCallback(
        (iframe: HTMLIFrameElement, content: string) => {
            if (!iframe) return

            const doc = iframe.contentDocument || iframe.contentWindow?.document
            if (!doc) return

            let bodyContent = ''
            if (cleanedBody && cleanedBody.trim() && cleanedBody.trim() !== 'No body') {
                bodyContent = cleanedBody
            } else if (content && content.trim() && content.trim() !== 'No body') {
                bodyContent = content
            } else if (snippet) {
                bodyContent = `<div style="font-family: Arial, sans-serif; line-height: 1.6;">${snippet}</div>`
            }

            if (!bodyContent) {
                bodyContent =
                    '<div style="font-family: Arial, sans-serif; color: var(--joy-palette-text-secondary, #666); font-style: italic;">No content available.</div>'
            }

            // Resolve Joy mode to 'light' | 'dark' for the helper
            const colorMode: 'light' | 'dark' = mode === 'dark' ? 'dark' : 'light'

            // 🔥 Build full HTML (sanitized + themed) via util
            const html = buildThemedEmailHtml(bodyContent, colorMode)

            doc.open()
            doc.write(html)
            doc.close()

            // Make external links open in new tab
            doc.querySelectorAll('a').forEach((link) => {
                if (link.host !== window.location.host) {
                    link.setAttribute('target', '_blank')
                    link.setAttribute('rel', 'noopener noreferrer')
                }
            })

            // Replace CID references with actual attachment URLs for inline images
            if (attachments && attachments.length > 0) {
                const inlineAttachments = attachments.filter((att) => att.is_inline && att.content_id)
                const foundIds = new Set<string>()

                if (inlineAttachments.length > 0) {
                    // Find all img tags with cid: sources
                    const images = doc.querySelectorAll('img[src^="cid:"]')

                    // Build list of images to load with their matched attachments
                    const imagesToLoad: { img: HTMLImageElement; attachment: FetchedEmailAttachment; contentId: string }[] = []
                    images.forEach((imgElement) => {
                        const img = imgElement as HTMLImageElement
                        const cidSrc = img.getAttribute('src')
                        if (!cidSrc) return

                        const contentId = cidSrc.replace('cid:', '')
                        const attachment = inlineAttachments.find((att) => {
                            if (!att.content_id) return false
                            const normalizedAttId = att.content_id.replace(/[<>]/g, '').trim().toLowerCase()
                            const normalizedContentId = contentId.trim().toLowerCase()
                            return (
                                normalizedAttId === normalizedContentId ||
                                normalizedAttId.includes(normalizedContentId) ||
                                normalizedContentId.includes(normalizedAttId)
                            )
                        })

                        if (attachment) {
                            foundIds.add(attachment.id)
                            imagesToLoad.push({ img, attachment, contentId })
                        }
                    })

                    // Update state with found inline image IDs
                    setFoundInlineImageIds(foundIds)

                    // Build batch requests and load via batch endpoint
                    const imgMap = new Map<string, HTMLImageElement[]>()
                    const requests: AttachmentRequest[] = []
                    for (const { img, attachment } of imagesToLoad) {
                        const key = attachment.id
                        if (!imgMap.has(key)) {
                            imgMap.set(key, [])
                            requests.push({ attachmentId: attachment.id, messageId, grantId: attachment.grant_id })
                        }
                        imgMap.get(key)!.push(img)
                    }

                    const errorPlaceholder =
                        'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjVmNWY1IiBzdHJva2U9IiNkZGQiLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjE0IiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+RXJyb3IgbG9hZGluZyBpbWFnZTwvdGV4dD48L3N2Zz4='

                    loadAttachmentsInBatches(
                        requests,
                        attachmentCache.current,
                        (attachmentId, dataUrl) => {
                            const imgs = imgMap.get(attachmentId)
                            if (imgs) {
                                for (const img of imgs) {
                                    img.src = dataUrl
                                    img.style.maxWidth = '100%'
                                    img.style.height = 'auto'
                                }
                            }
                        },
                        (attachmentId) => {
                            const imgs = imgMap.get(attachmentId)
                            if (imgs) {
                                for (const img of imgs) {
                                    img.src = errorPlaceholder
                                }
                            }
                        }
                    ).catch((error) => console.error('Failed to load inline image attachments', error))
                }
            }

            return doc.body
        },
        [mode, attachments, messageId, cleanedBody, snippet, attachmentCache]
    )

    useEffect(() => {
        if (!iframeRef.current || !hasContent()) return

        const iframe = iframeRef.current

        // Use external setup if provided, otherwise use internal
        const iframeBody = externalSetupIframeContent ? externalSetupIframeContent(iframe, body) : internalSetupIframeContent(iframe, body)

        const doc = iframe.contentDocument
        if (doc) {
            const bodyEl = doc.body
            if (bodyEl) {
                const html = bodyEl.innerHTML

                // Remove all nested Gmail quotes and replies, preserving only top message
                const tempDiv = document.createElement('div')
                tempDiv.innerHTML = html

                tempDiv.querySelectorAll('.gmail_quote, .gmail_quote_container, blockquote.gmail_quote').forEach((el) => {
                    el.remove()
                })

                // Also remove "Forwarded message" headers *inside quotes*
                tempDiv.querySelectorAll('*').forEach((el) => {
                    if (el.textContent?.includes('---------- Forwarded message ---------')) {
                        // If the entire block is a forwarded message (and not top-level)
                        // remove that inner forwarded section
                        const parentQuote = el.closest('.gmail_quote, .gmail_quote_container, blockquote.gmail_quote')
                        if (parentQuote) parentQuote.remove()
                    }
                })

                // Replace the iframe body with cleaned HTML
                bodyEl.innerHTML = tempDiv.innerHTML

                // Ensure external links open in new tab again
                bodyEl.querySelectorAll('a').forEach((link) => {
                    if (link.host !== window.location.host) {
                        link.setAttribute('target', '_blank')
                        link.setAttribute('rel', 'noopener noreferrer')
                    }
                })
            }
        }

        if (!iframeBody) return

        // Auto-resize logic
        let rafId: number
        const resizeObserver = new ResizeObserver((entries) => {
            if (rafId) cancelAnimationFrame(rafId)

            rafId = requestAnimationFrame(() => {
                if (!iframe.isConnected) return

                const contentHeight = Math.ceil(entries[0].contentRect.height)
                if (contentHeight > 0 && height === 'auto') {
                    iframe.style.height = `${contentHeight + 40}px`
                }
            })
        })

        resizeObserver.observe(iframeBody)

        return () => {
            resizeObserver.disconnect()
            if (rafId) cancelAnimationFrame(rafId)
        }
    }, [hasContent, height, externalSetupIframeContent, internalSetupIframeContent, body])

    return (
        <div style={{ width: '100%' }}>
            {/* Only show iframe if we have content */}
            {hasContent() && (
                <iframe
                    ref={iframeRef}
                    sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                    style={{
                        width: width,
                        border: 'none',
                        height: height,
                    }}
                    title={`Email: ${subject || 'No subject'}`}
                />
            )}

            {/* Render image previews outside iframe for:
          1. Non-inline images
          2. Inline images that were NOT found/rendered in the HTML body */}
            {attachments &&
                attachments.length > 0 &&
                (() => {
                    const imageAttachmentsToShow = attachments.filter((att) => {
                        // Must be an image
                        if (!att.content_type || !att.content_type.startsWith('image/')) {
                            return false
                        }

                        // Show non-inline images
                        if (!att.is_inline) {
                            return true
                        }

                        // Show inline images that were NOT found in HTML body
                        if (att.is_inline && !foundInlineImageIds.has(att.id)) {
                            return true
                        }

                        return false
                    })

                    return imageAttachmentsToShow.length > 0 ? (
                        <ImagePreviewsOutsideIframe attachments={imageAttachmentsToShow} messageId={messageId} cache={attachmentCache} />
                    ) : null
                })()}
        </div>
    )
}

export default EmailBody
